import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';

/**
 * Service for managing the state of a modal.
 * This service tracks whether the modal is open or closed.
 */
@Injectable({ providedIn: 'any' })
export class MpModalService {

  // Internal flag to track if the modal is open or closed
  private static _isModalOpened = false;

  private _saveSubject = new Subject<any>();
  public onSave$ = this._saveSubject.asObservable();

  private _cancelSubject = new Subject<any>();
  public onCancel$ = this._cancelSubject.asObservable();

  private _saveButtonStateSubject = new BehaviorSubject<boolean>(true);
  public saveButtonState$ = this._saveButtonStateSubject.asObservable();

  private _saveButtonLoadingSubject = new BehaviorSubject<boolean>(false);
  public _saveButtonLoading$ = this._saveButtonLoadingSubject.asObservable();

  /**
   * Gets the current state of the modal (open or closed).
   *
   * @returns {boolean} - Returns true if the modal is open, false if it is closed.
   */
  static isModalOpened(): boolean {
    return MpModalService._isModalOpened;
  }

  close: () => {};

  /**
   * Sets the modal state to open.
   * This method marks the modal as open, setting the internal flag to true.
   */
  public setModalOpen() {
    MpModalService._isModalOpened = true;
  }

  /**
   * Sets the modal state to closed.
   * This method marks the modal as closed, setting the internal flag to false.
   */
  public setModalClosed() {
    MpModalService._isModalOpened = false;
  }

  /**
   * Emits a "save" action event.
   *
   * This method triggers the save action by emitting a `null` value to the save subject.
   * All subscribers to the save event will be notified when this method is called.
   */
  public save() {
    this._saveSubject.next(null);
  }

  /**
   * Emits a "cancel" action event.
   *
   * This method triggers the cancel action by emitting a `null` value to the cancel subject.
   * All subscribers to the cancel event will be notified when this method is called.
   */
  public cancel() {
    this._cancelSubject.next(null);
  }


  /**
   * Updates the state of the save button.
   *
   * This method emits the new state of the save button to all subscribers.
   * It triggers a change in the button's state (enabled/disabled).
   *
   * @param {boolean} state - The new state of the save button.
   * `true` to enable the button, `false` to disable it.
   */
  public setSaveButtonState(state: boolean) {
    this._saveButtonStateSubject.next(state);
  }

  /**
   * Updates the loading state of the save button.
   *
   * This method triggers the "loading" state of the save button.
   * When `true`, the button shows as loading (disabled or with loading spinner).
   * When `false`, the loading state is removed.
   *
   * @param {boolean} isLoading - The new loading state.
   * `true` to indicate loading, `false` to remove loading state.
   */
  public setSaveButtonLoading(isLoading: boolean) {
    this._saveButtonLoadingSubject.next(isLoading);
  }

  /**
   * Retrieves the current loading state of the save button.
   *
   * This getter returns the current loading state as a boolean,
   * indicating whether the button is currently in a loading state.
   *
   * @returns {boolean} - Returns the current loading state of the save button.
   */
  public isSaveButtonLoading(): boolean {
    return this._saveButtonLoadingSubject.value;
  }
}
